<template>
  <div class="mm">
    <!-- Account -->
    <section class="mm-account">
      <button
        v-if="isLogin"
        type="button"
        class="mm-account__card"
        @click="onOpenAccount"
      >
        <span class="mm-account__avatar">{{ avatarInitial }}</span>
        <span class="mm-account__meta">
          <strong>{{ userTagName || $t('header.account') }}</strong>
          <span>{{ $t('header.viewAccount') }}</span>
        </span>
        <i class="fa-solid fa-chevron-right mm-account__chevron" aria-hidden="true" />
      </button>

      <div v-else class="mm-account__guest">
        <button type="button" class="mm-btn mm-btn--primary" @click="goLogin">
          {{ $t('text.login') }}
        </button>
        <button type="button" class="mm-btn mm-btn--ghost" @click="goRegister">
          {{ $t('text.register') }}
        </button>
      </div>
    </section>

    <!-- Primary actions -->
    <section class="mm-group mm-group--actions" :aria-label="$t('header.quickActions')">
      <button type="button" class="mm-item mm-item--cta" @click="goPostNews">
        <span class="mm-item__icon" aria-hidden="true">
          <i class="fa-solid fa-plus" />
        </span>
        <span>{{ $t('header.create') }}</span>
      </button>
      <button type="button" class="mm-item" @click="goRooms">
        <span class="mm-item__icon" aria-hidden="true">
          <i class="fa-solid fa-house-circle-check" />
        </span>
        <span>{{ $t('header.manageRooms') }}</span>
      </button>
      <button type="button" class="mm-item" @click="onOpenNotifications">
        <span class="mm-item__icon" aria-hidden="true">
          <i class="fa-regular fa-bell" />
        </span>
        <span>{{ $t('header.notifications') }}</span>
        <span v-if="hasUnread" class="mm-item__dot" aria-hidden="true" />
      </button>
      <button type="button" class="mm-item" @click="goDailyPoints">
        <span class="mm-item__icon" aria-hidden="true">
          <i class="fa-solid fa-gift" />
        </span>
        <span>{{ $t('loginGetPoint.title') }}</span>
      </button>
      <button type="button" class="mm-item" @click="goFavourite">
        <span class="mm-item__icon" aria-hidden="true">
          <i class="fa-solid fa-heart" />
        </span>
        <span>{{ $t('text.favourite') }}</span>
      </button>
    </section>

    <!-- CMS navigation -->
    <section v-if="dataMenu.length" class="mm-group mm-group--nav">
      <p class="mm-group__label">{{ $t('header.browse') }}</p>
      <a-menu mode="inline" :selectedKeys="[active]" class="mobile-menu">
        <template v-for="item in dataMenu" :key="item.id || item.linkTo">
          <a-sub-menu v-if="item.children?.length" :key="`sub-${item.id}`">
            <template #title>
              <span>
                <i v-if="item.icon" :class="item.icon" />
                {{ menuLabel(item.linkLabel) }}
              </span>
            </template>

            <template v-for="child in item.children" :key="child.id || child.linkTo">
              <a-sub-menu v-if="child.children?.length" :key="`sub-${child.id}`">
                <template #title>{{ menuLabel(child.linkLabel) }}</template>
                <a-menu-item
                  v-for="sub in child.children"
                  :key="sub.linkTo"
                  @click="go(sub.linkTo)"
                >
                  {{ menuLabel(sub.linkLabel) }}
                </a-menu-item>
              </a-sub-menu>

              <a-menu-item v-else :key="child.linkTo" @click="go(child.linkTo)">
                {{ menuLabel(child.linkLabel) }}
              </a-menu-item>
            </template>
          </a-sub-menu>

          <a-menu-item v-else :key="item.linkTo" @click="go(item.linkTo)">
            <i v-if="item.icon" :class="item.icon" />
            {{ menuLabel(item.linkLabel) }}
          </a-menu-item>
        </template>
      </a-menu>
    </section>

    <!-- Utilities -->
    <section class="mm-group mm-group--utils">
      <div class="mm-item mm-item--static">
        <span class="mm-item__icon" aria-hidden="true">
          <i class="fa-solid fa-globe" />
        </span>
        <span>{{ $t('header.language') }}</span>
        <a-switch
          class="mm-lang-switch"
          size="small"
          :checked="isLangVn"
          checked-children="VN"
          un-checked-children="EN"
          @change="onLangSwitch"
        />
      </div>

      <a
        v-if="hotlineHref"
        class="mm-item"
        :href="hotlineHref"
      >
        <span class="mm-item__icon" aria-hidden="true">
          <i class="fa-solid fa-phone" />
        </span>
        <span>{{ $t('text.contactHotline') }}</span>
        <span class="mm-item__trail">{{ hotlineDisplay }}</span>
      </a>

      <button
        v-if="isLogin"
        type="button"
        class="mm-item mm-item--danger"
        @click="logOut"
      >
        <span class="mm-item__icon" aria-hidden="true">
          <i class="fa-solid fa-arrow-right-from-bracket" />
        </span>
        <span>{{ $t('text.logOut') }}</span>
      </button>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import commons from '@/utils/Common'
import {
  CUSTOMER_POST_NEWS,
  FAVOURITE,
  HOME,
  LOGIN,
  LOGIN_DAILY_POINT,
  REGISTERS,
  ROOM_MANAGER,
} from '@/const/path'

interface Props {
  dataMenu?: any[]
  isLogin?: boolean
  userTagName?: string
  hasUnread?: boolean
  langLabelShort?: string
  hotline?: string
}

const props = withDefaults(defineProps<Props>(), {
  dataMenu: () => [],
  isLogin: false,
  userTagName: '',
  hasUnread: false,
  langLabelShort: 'VN',
  hotline: '',
})

const emit = defineEmits<{
  close: []
  'open-notifications': []
  'open-account': []
  'toggle-lang': []
  'change-lang': [lang: 'vn' | 'us']
  'logged-out': []
}>()

const { t, te, locale } = useI18n()
const router = useRouter()
const active = router.currentRoute.value.path

const avatarInitial = computed(() => {
  const name = (props.userTagName || 'U').trim()
  return name.charAt(0).toUpperCase() || 'U'
})

const hotlineDisplay = computed(() => String(props.hotline || '').trim())
const hotlineHref = computed(() => {
  const digits = hotlineDisplay.value.replace(/[^\d+]/g, '')
  return digits ? `tel:${digits}` : ''
})

const isLangVn = computed(() => {
  const code = locale.value || (props.langLabelShort === 'EN' ? 'us' : 'vn')
  return code !== 'us'
})

const menuLabel = (linkLabel?: string) => {
  const label = String(linkLabel || '').trim()
  if (!label) return ''
  return te(label) ? t(label) : label
}

const closeThen = (fn: () => void) => {
  emit('close')
  fn()
}

const go = (path: string) => {
  if (!path) return
  closeThen(() => {
    router.push(path).catch(() => undefined)
  })
}

const requireLoginThen = (path: string) => {
  if (!props.isLogin) {
    go(LOGIN)
    return
  }
  go(path)
}

const goLogin = () => go(LOGIN)
const goRegister = () => go(REGISTERS)
const goPostNews = () => requireLoginThen(CUSTOMER_POST_NEWS)
const goRooms = () => requireLoginThen(ROOM_MANAGER)
const goDailyPoints = () => go(LOGIN_DAILY_POINT)
const goFavourite = () => go(FAVOURITE)

const onOpenAccount = () => {
  emit('close')
  emit('open-account')
}

const onOpenNotifications = () => {
  if (!props.isLogin) {
    go(LOGIN)
    return
  }
  emit('close')
  emit('open-notifications')
}

const onLangSwitch = (checked: boolean) => {
  emit('change-lang', checked ? 'vn' : 'us')
}

const logOut = () => {
  emit('close')
  commons.logOut()
  emit('logged-out')
  router.push({ path: HOME }).catch(() => undefined)
}
</script>

<style scoped>
.mm {
  --mm-gold: #d4b57a;
  --mm-gold-soft: rgba(212, 181, 122, 0.18);
  --mm-gold-strong: rgba(212, 181, 122, 0.28);
  --mm-text: rgba(255, 255, 255, 0.96);
  --mm-muted: rgba(255, 255, 255, 0.62);
  --mm-line: rgba(255, 255, 255, 0.12);
  --mm-ink: #102542;
  --mm-surface: rgba(255, 255, 255, 0.07);
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
  padding-bottom: 28px;
  color: var(--mm-text);
  color-scheme: dark;
}

.mm button,
.mm a.mm-item {
  -webkit-tap-highlight-color: transparent;
}

.mm-account__card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--mm-line);
  border-radius: 14px;
  background: var(--mm-surface);
  color: var(--mm-text) !important;
  text-align: left;
  cursor: pointer;
}

.mm-account__card:hover {
  background: rgba(255, 255, 255, 0.11);
}

.mm-account__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #f59e0b;
  color: #fff !important;
  font-size: 15px;
  font-weight: 700;
  flex: 0 0 auto;
}

.mm-account__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.mm-account__meta strong {
  font-size: 15px;
  font-weight: 700;
  color: var(--mm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mm-account__meta span {
  font-size: 12px;
  color: var(--mm-muted);
}

.mm-account__chevron {
  color: var(--mm-muted);
  font-size: 12px;
}

.mm-account__guest {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.mm-btn {
  height: 44px;
  border-radius: 22px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mm-btn--primary {
  border: none !important;
  background: var(--mm-gold) !important;
  color: var(--mm-ink) !important;
  box-shadow: 0 4px 14px rgba(212, 181, 122, 0.28);
}

.mm-btn--primary:hover {
  background: #e2c690 !important;
  color: var(--mm-ink) !important;
}

.mm-btn--ghost {
  border: 1.5px solid rgba(255, 255, 255, 0.45) !important;
  background: transparent !important;
  color: #fff !important;
}

.mm-btn--ghost:hover {
  border-color: #fff !important;
  background: rgba(255, 255, 255, 0.08) !important;
  color: #fff !important;
}

.mm-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid var(--mm-line);
}

.mm-group--actions {
  padding-top: 4px;
  border-top: none;
}

.mm-group__label {
  margin: 0 0 8px;
  padding: 0 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.48);
}

.mm-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 11px 12px;
  border: none !important;
  border-radius: 12px;
  background: transparent !important;
  color: var(--mm-text) !important;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease;
}

.mm-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  flex: 0 0 auto;
}

.mm-item__icon i,
.mm-item i {
  width: auto;
  margin: 0;
  color: var(--mm-gold) !important;
  font-size: 14px;
  line-height: 1;
}

.mm-item > span:not(.mm-item__icon):not(.mm-item__dot):not(.mm-item__trail) {
  color: inherit;
}

.mm-item:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #fff !important;
}

.mm-item--cta {
  background: var(--mm-gold-soft) !important;
  border: 1px solid rgba(212, 181, 122, 0.35) !important;
  color: #fff !important;
}

.mm-item--cta .mm-item__icon {
  background: var(--mm-gold);
}

.mm-item--cta .mm-item__icon i {
  color: var(--mm-ink) !important;
}

.mm-item--cta:hover {
  background: var(--mm-gold-strong) !important;
}

.mm-item--danger {
  color: #ffc9c9 !important;
}

.mm-item--danger .mm-item__icon {
  background: rgba(255, 120, 120, 0.12);
}

.mm-item--danger .mm-item__icon i {
  color: #ffb4b4 !important;
}

.mm-item--static {
  cursor: default;
}

.mm-item--static:hover {
  background: transparent !important;
}

.mm-lang-switch {
  margin-left: auto;
}

.mm-lang-switch :deep(.ant-switch-inner) {
  font-size: 11px;
  font-weight: 700;
}

.mm-lang-switch.ant-switch-checked {
  background: var(--mm-gold) !important;
}

.mm-item__trail {
  margin-left: auto;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.78) !important;
  letter-spacing: 0.02em;
}

.mm-item__dot {
  margin-left: auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #e53935;
  box-shadow: 0 0 0 2px rgba(229, 57, 53, 0.25);
}

.mobile-menu {
  border-right: none !important;
  background: transparent !important;
  color: var(--mm-text);
  padding: 0;
}

.mobile-menu :deep(.ant-menu-item),
.mobile-menu :deep(.ant-menu-submenu-title) {
  height: 48px !important;
  line-height: 48px !important;
  margin: 0 0 2px !important;
  padding: 0 12px !important;
  border-radius: 12px !important;
  color: var(--mm-text) !important;
  font-weight: 600;
}

.mobile-menu :deep(.ant-menu-item i),
.mobile-menu :deep(.ant-menu-submenu-title i) {
  color: var(--mm-gold) !important;
  margin-right: 10px;
  font-size: 14px;
}

.mobile-menu :deep(.ant-menu-submenu-arrow) {
  color: rgba(255, 255, 255, 0.55) !important;
}

.mobile-menu :deep(.ant-menu-item:hover),
.mobile-menu :deep(.ant-menu-submenu-title:hover) {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.08) !important;
}

.mobile-menu :deep(.ant-menu-item-selected) {
  background: var(--mm-gold-soft) !important;
  color: #fff !important;
  box-shadow: inset 3px 0 0 var(--mm-gold);
}

.mobile-menu :deep(.ant-menu-item-selected),
.mobile-menu :deep(.ant-menu-item-selected a),
.mobile-menu :deep(.ant-menu-item-selected span) {
  color: #fff !important;
}

.mobile-menu :deep(.ant-menu-item::after) {
  border-right: 0 !important;
}

.mobile-menu :deep(.ant-menu-sub) {
  background: transparent !important;
}

.mobile-menu :deep(.ant-menu-item-active),
.mobile-menu :deep(.ant-menu-submenu-active > .ant-menu-submenu-title) {
  color: #fff !important;
}
</style>
